import React from "react";

type ButtonVariant = "filled" | "outlined" | "gradient" | "text";
type ButtonSize = "sm" | "md" | "lg";
type ButtonColor = "blue-gray" | "blue" | "red" | "green" | "orange" | "gray" | "white";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  color?: ButtonColor;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, (color: ButtonColor) => string> = {
  filled: (color: ButtonColor) => {
    const colorMap: Record<ButtonColor, string> = {
      "blue-gray": "bg-blue-gray-500 text-white shadow-md hover:shadow-lg hover:bg-blue-gray-600",
      blue: "bg-blue-500 text-white shadow-md hover:shadow-lg hover:bg-blue-600",
      red: "bg-red-500 text-white shadow-md hover:shadow-lg hover:bg-red-600",
      green: "bg-green-500 text-white shadow-md hover:shadow-lg hover:bg-green-600",
      orange: "bg-orange-500 text-white shadow-md hover:shadow-lg hover:bg-orange-600",
      gray: "bg-gray-500 text-white shadow-md hover:shadow-lg hover:bg-gray-600",
      white: "bg-white text-blue-gray-900 shadow-md hover:shadow-lg hover:bg-gray-50",
    };
    return colorMap[color];
  },
  outlined: (color: ButtonColor) => {
    const colorMap: Record<ButtonColor, string> = {
      "blue-gray": "border border-blue-gray-500 text-blue-gray-500 hover:bg-blue-gray-50",
      blue: "border border-blue-500 text-blue-500 hover:bg-blue-50",
      red: "border border-red-500 text-red-500 hover:bg-red-50",
      green: "border border-green-500 text-green-500 hover:bg-green-50",
      orange: "border border-orange-500 text-orange-500 hover:bg-orange-50",
      gray: "border border-gray-500 text-gray-500 hover:bg-gray-50",
      white: "border border-white text-white hover:bg-white/10",
    };
    return colorMap[color];
  },
  gradient: () => {
    return "bg-gradient-to-tr from-blue-600 to-blue-400 text-white shadow-md hover:shadow-lg";
  },
  text: (color: ButtonColor) => {
    const colorMap: Record<ButtonColor, string> = {
      "blue-gray": "text-blue-gray-500 hover:bg-blue-gray-50",
      blue: "text-blue-500 hover:bg-blue-50",
      red: "text-red-500 hover:bg-red-50",
      green: "text-green-500 hover:bg-green-50",
      orange: "text-orange-500 hover:bg-orange-50",
      gray: "text-gray-500 hover:bg-gray-50",
      white: "text-white hover:bg-white/10",
    };
    return colorMap[color];
  },
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-4 py-2 text-xs",
  md: "px-6 py-3 text-xs",
  lg: "px-7 py-3.5 text-sm",
};

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "filled",
  size = "md",
  color = "blue-gray",
  fullWidth = false,
  className = "",
  ...props
}) => {
  const variantClass = variantClasses[variant](color);
  const sizeClass = sizeClasses[size];
  const widthClass = fullWidth ? "w-full" : "";

  return (
    <button
      className={`align-middle select-none font-sans font-bold text-center uppercase transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none rounded-lg ${variantClass} ${sizeClass} ${widthClass} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};